import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, MapPin, Clock, Check, Utensils, ImagePlus } from "lucide-react";
import {
  listBreakfast,
  createBreakfastBulk,
  updateBreakfastGroup,
  deleteBreakfast,
  KNOWN_KINDS,
  type BreakfastOption,
  type BreakfastGroupWrite,
  type BreakfastBulkWrite,
} from "@/lib/breakfast-api";

/** Editing an existing group edits its shared serving details; dishes are read-only here. */
type Draft = {
  id: string;
  kind: string;
  serving_time: string;
  serving_location: string;
  is_active: boolean;
  /** Read-only list of dishes in this group, for display in the editor. */
  items: BreakfastOption["items"];
};

/** One dish being added in the bulk create form. */
type BulkItem = { name: string; imageFile: File | null };

/** The "Add Breakfast" form: shared serving fields + a list of dishes created together. */
type BulkDraft = {
  kind: string;
  serving_time: string;
  serving_location: string;
  is_active: boolean;
  items: BulkItem[];
};

const EMPTY_BULK: BulkDraft = {
  kind: "street_food",
  serving_time: "",
  serving_location: "",
  is_active: true,
  items: [{ name: "", imageFile: null }],
};

export const Route = createFileRoute("/admin/breakfast")({ component: BreakfastAdmin });

function BreakfastAdmin() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Draft | null>(null);
  const [bulk, setBulk] = useState<BulkDraft | null>(null);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-breakfast"],
    queryFn: listBreakfast,
  });

  // The API only exposes one kind today; merge in any others already present in the data so
  // existing rows stay editable without guessing at the backend's full enum.
  const kindOptions = [
    ...KNOWN_KINDS,
    ...list
      .filter((b) => b.kind && !KNOWN_KINDS.some((k) => k.value === b.kind))
      .map((b) => ({ value: b.kind, label: b.kind_display || b.kind })),
  ].filter((k, i, arr) => arr.findIndex((x) => x.value === k.value) === i);

  const toGroupWrite = (d: Draft): BreakfastGroupWrite => ({
    kind: d.kind.trim(),
    serving_time: d.serving_time.trim(),
    serving_location: d.serving_location.trim(),
    is_active: d.is_active,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-breakfast"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const toBulkWrite = (d: BulkDraft): BreakfastBulkWrite => ({
    kind: d.kind.trim(),
    serving_time: d.serving_time.trim(),
    serving_location: d.serving_location.trim(),
    is_active: d.is_active,
    items: d.items
      .filter((it) => it.name.trim())
      .map((it) => ({ name: it.name.trim(), imageFile: it.imageFile })),
  });

  const createBulkMut = useMutation({
    mutationFn: (d: BulkDraft) => createBreakfastBulk(toBulkWrite(d)),
    onSuccess: (group) => {
      invalidate();
      const n = group.items.length;
      toast.success(n > 1 ? `${n} breakfast items added` : "Breakfast added");
      setBulk(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (d: Draft) => updateBreakfastGroup(d.id, toGroupWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Breakfast group updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteBreakfast(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });

  const saving = updateMut.isPending;

  // The single editor only edits an existing group's serving details; creation goes through bulk.
  const save = () => {
    if (!edit?.id) {
      return;
    }
    if (!edit.kind.trim()) {
      toast.error("Kind is required");
      return;
    }
    updateMut.mutate(edit);
  };

  const saveBulk = () => {
    if (!bulk) {
      return;
    }
    if (!bulk.kind.trim()) {
      toast.error("Kind is required");
      return;
    }
    if (!bulk.items.some((it) => it.name.trim())) {
      toast.error("Add at least one item with a name");
      return;
    }
    createBulkMut.mutate(bulk);
  };

  const remove = (id: string) => {
    if (confirm("Delete this breakfast group and all its items?")) {
      deleteMut.mutate(id);
    }
  };

  const openEdit = (b: BreakfastOption) =>
    setEdit({
      id: b.id,
      kind: b.kind,
      serving_time: b.serving_time,
      serving_location: b.serving_location,
      is_active: b.is_active,
      items: b.items,
    });

  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold">Breakfast Options</h2>
          <p className="text-sm text-muted-foreground">
            Menus shown to users when they book a walk that includes breakfast.
          </p>
        </div>
        <button
          onClick={() => setBulk({ ...EMPTY_BULK, items: [{ name: "", imageFile: null }] })}
          className="btn-primary py-2 text-sm"
        >
          <Plus className="h-4 w-4" /> Add Breakfast
        </button>
      </div>

      {isLoading && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      )}
      {isError && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load breakfast options"}
        </div>
      )}
      {!isLoading && !isError && list.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No breakfast options yet. Click &ldquo;Add Breakfast&rdquo; to create one.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((b) => {
          // Fall back to the legacy top-level name when a group has no nested dishes yet.
          const dishes =
            b.items.length > 0
              ? b.items
              : b.name
                ? [{ id: b.id, name: b.name, image: b.image, display_number: 0 }]
                : [];
          return (
            <div
              key={b.id}
              className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card"
            >
              <div className="flex items-start justify-between gap-2 p-4 pb-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      {b.kind_display}
                    </span>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                        b.is_active
                          ? "border-primary/30 bg-primary/15 text-primary"
                          : "border-border bg-muted text-muted-foreground"
                      }`}
                    >
                      {b.is_active ? "Active" : "Hidden"}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-col gap-1 text-xs text-muted-foreground">
                    {b.serving_time ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3 shrink-0" /> {b.serving_time}
                      </span>
                    ) : null}
                    {b.serving_location ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3 shrink-0" /> {b.serving_location}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    onClick={() => openEdit(b)}
                    title="Edit serving details"
                    className="rounded-lg p-1.5 hover:bg-muted"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => remove(b.id)}
                    title="Delete group"
                    className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Dishes in this group */}
              <div className="mt-auto border-t border-border p-3">
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Utensils className="h-3 w-3" /> {dishes.length} item
                  {dishes.length === 1 ? "" : "s"}
                </div>
                {dishes.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No dishes.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {dishes.map((it) => (
                      <li key={it.id} className="flex items-center gap-2">
                        {it.image ? (
                          <img
                            src={it.image}
                            alt={it.name}
                            loading="lazy"
                            className="h-9 w-9 shrink-0 rounded-lg border border-border object-cover"
                          />
                        ) : (
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                            <Utensils className="h-4 w-4" />
                          </div>
                        )}
                        <span className="truncate text-sm font-medium">{it.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {edit && (
        <Editor
          draft={edit}
          setDraft={setEdit}
          kinds={kindOptions}
          onSave={save}
          onClose={() => setEdit(null)}
          saving={saving}
        />
      )}

      {bulk && (
        <BulkEditor
          draft={bulk}
          setDraft={setBulk}
          kinds={kindOptions}
          onSave={saveBulk}
          onClose={() => setBulk(null)}
          saving={createBulkMut.isPending}
        />
      )}
    </div>
  );
}

function Editor({
  draft,
  setDraft,
  kinds,
  onSave,
  onClose,
  saving,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  kinds: { value: string; label: string }[];
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const u = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-serif text-xl font-bold">
            <Utensils className="h-5 w-5 text-primary" />
            Edit Breakfast
          </h3>
          <button onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Kind" required>
            <select
              value={draft.kind}
              onChange={(e) => u({ kind: e.target.value })}
              className="binp"
            >
              {kinds.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Serving Time">
            <input
              value={draft.serving_time}
              onChange={(e) => u({ serving_time: e.target.value })}
              placeholder="e.g. 8:00 – 9:30 AM"
              className="binp"
            />
          </Field>
          <Field label="Serving Location" full>
            <input
              value={draft.serving_location}
              onChange={(e) => u({ serving_location: e.target.value })}
              className="binp"
            />
          </Field>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={(e) => u({ is_active: e.target.checked })}
          />
          Active (visible to users)
        </label>

        {/* Dishes are read-only here — add/remove via a new "Add Breakfast" group. */}
        <div className="mt-5">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Items ({draft.items.length})
          </div>
          {draft.items.length === 0 ? (
            <p className="text-xs text-muted-foreground">No dishes in this group.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {draft.items.map((it) => (
                <li
                  key={it.id}
                  className="flex items-center gap-2 rounded-full border border-border bg-muted/40 py-1 pl-1 pr-3"
                >
                  {it.image ? (
                    <img
                      src={it.image}
                      alt={it.name}
                      className="h-7 w-7 rounded-full border border-border object-cover"
                    />
                  ) : (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-card text-muted-foreground">
                      <Utensils className="h-3.5 w-3.5" />
                    </span>
                  )}
                  <span className="text-xs font-medium">{it.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="btn-outline justify-center py-2 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary justify-center py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            <Check className="h-4 w-4" />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <style>{`.binp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}`}</style>
    </div>
  );
}

function BulkEditor({
  draft,
  setDraft,
  kinds,
  onSave,
  onClose,
  saving,
}: {
  draft: BulkDraft;
  setDraft: (d: BulkDraft) => void;
  kinds: { value: string; label: string }[];
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const u = (patch: Partial<BulkDraft>) => setDraft({ ...draft, ...patch });
  const setItem = (i: number, patch: Partial<BulkItem>) =>
    setDraft({
      ...draft,
      items: draft.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    });
  const addItem = () =>
    setDraft({ ...draft, items: [...draft.items, { name: "", imageFile: null }] });
  const removeItem = (i: number) =>
    setDraft({ ...draft, items: draft.items.filter((_, idx) => idx !== i) });

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-serif text-xl font-bold">
            <Utensils className="h-5 w-5 text-primary" />
            Add Breakfast
          </h3>
          <button onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Shared serving details — apply to every dish added below. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Kind" required>
            <select
              value={draft.kind}
              onChange={(e) => u({ kind: e.target.value })}
              className="binp"
            >
              {kinds.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Serving Time">
            <input
              value={draft.serving_time}
              onChange={(e) => u({ serving_time: e.target.value })}
              placeholder="e.g. 8:00 – 9:30 AM"
              className="binp"
            />
          </Field>
          <Field label="Serving Location" full>
            <input
              value={draft.serving_location}
              onChange={(e) => u({ serving_location: e.target.value })}
              placeholder="e.g. Main Gate"
              className="binp"
            />
          </Field>
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={(e) => u({ is_active: e.target.checked })}
          />
          Active (visible to users)
        </label>

        {/* Dishes */}
        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Items
            </span>
            <button
              type="button"
              onClick={addItem}
              className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/15"
            >
              <Plus className="h-3.5 w-3.5" /> Add item
            </button>
          </div>

          <div className="space-y-2">
            {draft.items.map((it, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded-xl border border-border bg-background p-2"
              >
                {it.imageFile ? (
                  <img
                    src={URL.createObjectURL(it.imageFile)}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-lg border border-border object-cover"
                  />
                ) : (
                  <label className="flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:bg-muted">
                    <ImagePlus className="h-4 w-4" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setItem(i, { imageFile: e.target.files?.[0] ?? null })}
                    />
                  </label>
                )}
                <input
                  value={it.name}
                  onChange={(e) => setItem(i, { name: e.target.value })}
                  placeholder={`Item ${i + 1} name`}
                  className="binp flex-1"
                />
                {it.imageFile ? (
                  <button
                    type="button"
                    onClick={() => setItem(i, { imageFile: null })}
                    title="Remove photo"
                    className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => removeItem(i)}
                  disabled={draft.items.length === 1}
                  title="Remove item"
                  className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10 disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="btn-outline justify-center py-2 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary justify-center py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            <Check className="h-4 w-4" />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <style>{`.binp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}`}</style>
    </div>
  );
}

function Field({
  label,
  required,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
